// ============================================================================
// api_deviceMedia.ts
// Capture caméra/micro côté device
// ============================================================================

import React from 'react';
import { MediaDeviceEntry, DeviceMediaEnumResult, ActiveStreamDetails } from '../../dataTypes/liveStreamManagment/types_media';
import { DeviceMediaCallbacks } from '../../dataTypes/liveStreamManagment/types_callbacks';
import { replaceDeviceTrack } from './api_deviceWebRTC';

export const enumerateDeviceMediaDevices = async (
  callbacks: DeviceMediaCallbacks
): Promise<DeviceMediaEnumResult> => {
  const tmp = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
  tmp.getTracks().forEach(t => t.stop());

  const list    = await navigator.mediaDevices.enumerateDevices();
  const cameras = list
    .filter(d => d.kind === 'videoinput')
    .map(d => ({ deviceId: d.deviceId, label: d.label || `Caméra ${d.deviceId.slice(0, 8)}`, groupId: d.groupId }));
  const mics    = list
    .filter(d => d.kind === 'audioinput')
    .map(d => ({ deviceId: d.deviceId, label: d.label || `Micro ${d.deviceId.slice(0, 8)}`, groupId: d.groupId }));

  const backCamera      = cameras.find(c => /back|arrière|rear|environment/i.test(c.label));
  const defaultCameraId = (backCamera ?? cameras[0])?.deviceId ?? '';
  const defaultMicId    = mics[0]?.deviceId ?? '';

  callbacks.addSdpLog({
    direction: 'info', type: 'event',
    label: `📷 ${cameras.length} caméra(s) · 🎤 ${mics.length} micro(s) détecté(s)`,
  });

  return { cameras, mics, defaultCameraId, defaultMicId };
};

export const captureDeviceStream = async (
  cameraId: string,
  micId: string,
  pttOn: boolean,
  callbacks: DeviceMediaCallbacks
): Promise<MediaStream> => {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: cameraId
      ? { deviceId: { exact: cameraId }, width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } }
      : { facingMode: 'environment',     width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
    audio: micId
      ? { deviceId: { exact: micId }, echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      : { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });

  stream.getAudioTracks().forEach(t => { t.enabled = pttOn; });

  const vt = stream.getVideoTracks()[0];
  const at = stream.getAudioTracks()[0];
  if (vt) callbacks.addSdpLog({ direction: 'info', type: 'event', label: `📷 Caméra: ${vt.label}` });
  if (at) callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🎤 Micro: ${at.label}` });

  return stream;
};

export const changeDeviceCamera = async (
  cameraId: string,
  micId: string,
  pttOn: boolean,
  pc: RTCPeerConnection | null,
  streamRef: React.MutableRefObject<MediaStream | null>,
  videoEl: HTMLVideoElement | null,
  callbacks: DeviceMediaCallbacks,
  onNewStream: (stream: MediaStream) => void
): Promise<void> => {
  streamRef.current?.getTracks().forEach(t => t.stop());

  const newStream = await captureDeviceStream(cameraId, micId, pttOn, callbacks);
  streamRef.current = newStream;
  if (videoEl) videoEl.srcObject = newStream;
  onNewStream(newStream);

  if (pc?.connectionState === 'connected') {
    for (const track of newStream.getTracks()) {
      await replaceDeviceTrack(pc, track, callbacks);
    }
  }

  callbacks.addSdpLog({ direction: 'info', type: 'event', label: `📷 Caméra changée → ${cameraId.slice(0, 16)}` });
};

export const changeDeviceMic = async (
  micId: string,
  pttOn: boolean,
  pc: RTCPeerConnection | null,
  streamRef: React.MutableRefObject<MediaStream | null>,
  callbacks: DeviceMediaCallbacks
): Promise<void> => {
  const newMicStream = await navigator.mediaDevices.getUserMedia({
    audio: { deviceId: { exact: micId }, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });

  const newTrack = newMicStream.getAudioTracks()[0];
  if (!newTrack) {
    callbacks.addSdpLog({ direction: 'received', type: 'error', label: '❌ changeDeviceMic: aucune track audio' });
    return;
  }

  newTrack.enabled = pttOn;

  if (streamRef.current) {
    const oldTrack = streamRef.current.getAudioTracks()[0];
    if (oldTrack) { streamRef.current.removeTrack(oldTrack); oldTrack.stop(); }
    streamRef.current.addTrack(newTrack);
  }

  if (pc?.connectionState === 'connected') {
    await replaceDeviceTrack(pc, newTrack, callbacks);
  }

  callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🎤 Micro changé: ${newTrack.label}` });
};

export const collectActiveStreamDetails = async (
  stream: MediaStream
): Promise<ActiveStreamDetails> => {
  let cameras: MediaDeviceEntry[] = [];
  let mics:    MediaDeviceEntry[] = [];

  try {
    const list = await navigator.mediaDevices.enumerateDevices();
    cameras = list.filter(d => d.kind === 'videoinput').map(d => ({ deviceId: d.deviceId, label: d.label || d.deviceId.slice(0, 16), groupId: d.groupId }));
    mics    = list.filter(d => d.kind === 'audioinput').map(d => ({ deviceId: d.deviceId, label: d.label || d.deviceId.slice(0, 16), groupId: d.groupId }));
  } catch { /* best-effort */ }

  const vt  = stream.getVideoTracks()[0];
  const at  = stream.getAudioTracks()[0];
  const vs  = (vt?.getSettings() ?? {}) as any;
  const as_ = (at?.getSettings() ?? {}) as any;

  return {
    cameras,
    mics,
    activeVideo: vt ? {
      label: vt.label, width: vs.width ?? 0, height: vs.height ?? 0,
      frameRate: vs.frameRate ?? 0, facingMode: vs.facingMode ?? 'N/A', deviceId: vs.deviceId ?? '',
    } : null,
    activeMic: at ? {
      label: at.label, sampleRate: as_.sampleRate ?? 0, channelCount: as_.channelCount ?? 0,
      echoCancellation: as_.echoCancellation ?? false, noiseSuppression: as_.noiseSuppression ?? false,
    } : null,
  };
};

export const releaseDeviceStream = (
  streamRef: React.MutableRefObject<MediaStream | null>,
  videoEl: HTMLVideoElement | null,
  callbacks: DeviceMediaCallbacks
): void => {
  if (streamRef.current) {
    streamRef.current.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: '⏹ Stream caméra/micro libéré' });
  }
  if (videoEl) videoEl.srcObject = null;
};
